import { Link } from 'react-router';
import { ArrowIcon } from '@/components/ui/Button';
import { Photo } from '@/components/ui/Photo';
import { Reveal } from '@/components/ui/Reveal';
import { SectionIntro } from '@/components/ui/SectionIntro';
import { CAPABILITIES } from '@/features/site/editorial';
import { useSite } from '@/features/site/SiteProvider';
import { cn } from '@/lib/utils';

// Column count follows the number of verified facts, so the grid never shows empty cells.
const FACT_COLUMNS: Record<number, string> = { 1: 'sm:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'lg:grid-cols-4' };

/** Section A — manufacturing capabilities (editorial 3-column grid). */
export function Capabilities() {
  const { trustBadges } = useSite().content;
  return (
    <section id="capabilities" aria-labelledby="capabilities-title" className="section-y bg-white">
      <div className="container-page">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <SectionIntro
            className="lg:col-span-7"
            id="capabilities-title"
            eyebrow="Manufacturing capabilities"
            title="Apparel and sports goods, produced to your specification."
          />
          <p className="text-[16px] leading-[1.7] text-muted lg:col-span-5">
            Two specialised manufacturing cities, one export partner. Every order is made to order for your label — from tech pack and sampling
            through bulk production, quality control and export packing.
          </p>
        </div>

        {trustBadges.items.length > 0 && (
          <ul className={cn('mt-12 grid gap-px overflow-hidden rounded-[8px] border border-line bg-line sm:grid-cols-2', FACT_COLUMNS[Math.min(trustBadges.items.length, 4)])} aria-label="Key facts">
            {trustBadges.items.map((b) => (
              <li key={b.title} className="bg-white p-5">
                <p className="font-display text-[15px] font-bold text-primary">{b.title}</p>
                <p className="mt-1 text-[13.5px] text-muted">{b.description}</p>
              </li>
            ))}
          </ul>
        )}

        <ul className="mt-14 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {CAPABILITIES.map((c, i) => (
            <Reveal as="li" key={c.title} delay={(i % 3) * 90}>
              <Link to={c.to} className="group block">
                <div className="aspect-[4/3] overflow-hidden rounded-[8px] bg-surface">
                  <Photo name={c.photo} zoom sizes="(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw" />
                </div>
                <div className="mt-6 flex items-baseline gap-4">
                  <span className="font-display text-[13px] font-bold text-accent-dark tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className="font-display text-[21px] font-bold tracking-tight text-primary">{c.title}</h3>
                    <p className="mt-2.5 text-[15px] leading-[1.65] text-muted">{c.description}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-[14px] font-semibold text-primary underline-offset-4 group-hover:underline">
                      {c.linkLabel}
                      <ArrowIcon />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}
